/* eslint-disable react/no-array-index-key */
/* eslint-disable react/forbid-prop-types */
import { SyntheticEvent, useCallback, useEffect, useState } from "react";
import "react-image-crop/dist/ReactCrop.css";
import Modal from "common/components/Modal/Modal";
import ReactCrop from "react-image-crop";
import { t } from "i18next";

export type CropModalProps = {
  tmpFiles: Array<any>;
  setTmpFiles: (value: any) => void;
  confirmModal: (value: any) => void;
  closeModal: () => void;
  visible?: boolean;
  maxWidth?: number;
  maxHeight?: number;
};

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = url;
    img.onload = () => resolve(img);
    img.onerror = reject;
  });
}

function CropModal({
  tmpFiles,
  setTmpFiles,
  confirmModal,
  maxWidth = 1600,
  maxHeight = 1200,
  closeModal,
  visible = true,
}: CropModalProps) {
  const [canvasUrls, setCanvasUrls] = useState<string[]>([]);

  const generateCanvasImage = async (
    url: string,
    aspect: number
  ): Promise<string> => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Failed to get canvas context");

    const image = await loadImage(url);

    canvas.width = maxWidth;
    canvas.height = maxHeight;
    // console.log({ w: canvas.width, h: canvas.height, aspect });

    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const scale = Math.min(
      canvas.width / image.width,
      canvas.height / image.height
    );
    const newWidth = image.width * scale;
    const newHeight = image.height * scale;
    const offsetX = (canvas.width - newWidth) / 2;
    const offsetY = (canvas.height - newHeight) / 2;

    ctx.drawImage(image, offsetX, offsetY, newWidth, newHeight);

    return canvas.toDataURL("image/png");
  };

  useEffect(() => {
    const initCanvas = async () => {
      if (visible && tmpFiles && canvasUrls.length === 0) {
        const newCanvasUrls = await Promise.all(
          tmpFiles.map((file) => {
            return generateCanvasImage(file.url, file.crop.aspect);
          })
        );
        setCanvasUrls(newCanvasUrls);
      }
    };
    initCanvas();
  }, [visible, canvasUrls, tmpFiles]);

  const handleImageLoaded = useCallback(
    (filekey: any, image: HTMLImageElement) => {
      console.log(tmpFiles, filekey);
      tmpFiles[filekey].image = image;
      tmpFiles[filekey].crop = {
        ...tmpFiles[filekey].crop,
        x: 0,
        y: 0,
        width: image.width,
        height: image.height,
      };
      setTmpFiles([...tmpFiles]);
    },
    [tmpFiles]
  );

  const handleCropChange = useCallback(
    (index: number, crop: any) => {
      tmpFiles[index].crop = { ...crop, aspect: tmpFiles[index].crop.aspect };
      setTmpFiles([...tmpFiles]);
    },
    [tmpFiles, setTmpFiles]
  );

  const handleConfirm = () => {
    setCanvasUrls([]);
    confirmModal(tmpFiles);
  };

  const handleClose = () => {
    setCanvasUrls([]);
    closeModal();
  };

  return (
    <Modal
      onConfirmModal={handleConfirm}
      onCloseModal={handleClose}
      visible={visible}
    >
      <div
        style={{
          height: "100%",
          width: "500px",
          padding: 10,
          overflowY: "auto",
        }}
      >
        {canvasUrls.map((canvasUrl, key) => (
          <ReactCrop
            key={key}
            crop={tmpFiles[key]?.crop}
            onChange={(newCrop: any) =>
              handleCropChange(tmpFiles[key].index, newCrop)
            }
            aspect={tmpFiles[key].crop.aspect}
          >
            <img
              alt="crop"
              src={canvasUrl}
              onLoad={(event: SyntheticEvent<HTMLImageElement, Event>) =>
                handleImageLoaded(key, event.currentTarget)
              }
            />
          </ReactCrop>
        ))}
      </div>
    </Modal>
  );
}

export default CropModal;
